import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { UpdateLapRequest } from "@/types";
import { createCrudHooks } from "@/hooks/crudHooksFactory";

export function useLaps(sessionId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "laps"],
    queryFn: () => apiClient.getLaps(sessionId),
    enabled: !!sessionId,
  });
}

export function useLap(sessionId: string, lapId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "laps", lapId],
    queryFn: () => apiClient.getLap(sessionId, lapId),
    enabled: !!sessionId && !!lapId,
  });
}

export function useLapStatistics(sessionId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "laps", "statistics"],
    queryFn: () => apiClient.getLapStatistics(sessionId),
    enabled: !!sessionId,
  });
}

export function useBestLap(sessionId: string) {
  return useQuery({
    queryKey: ["sessions", sessionId, "laps", "best"],
    queryFn: () => apiClient.getBestLap(sessionId),
    enabled: !!sessionId,
  });
}

const lapCrudHooks = createCrudHooks({
  update: {
    mutationFn: ({
      sessionId,
      lapId,
      ...lapData
    }: { sessionId: string; lapId: string } & Partial<UpdateLapRequest>) =>
      apiClient.updateLap(sessionId, lapId, lapData),
    cacheActions: [
      // Invalidate related queries
      {
        type: "invalidate",
        key: (updatedLap) => ["sessions", updatedLap.sessionId, "laps"],
      },
      {
        type: "invalidate",
        key: (updatedLap) => [
          "sessions",
          updatedLap.sessionId,
          "laps",
          updatedLap.id,
        ],
      },
      {
        type: "invalidate",
        key: (updatedLap) => [
          "sessions",
          updatedLap.sessionId,
          "laps",
          "statistics",
        ],
      },
      {
        type: "invalidate",
        key: (updatedLap) => ["sessions", updatedLap.sessionId, "laps", "best"],
      },
      { type: "invalidate", key: ["dashboard"] },
    ],
  },
  delete: {
    mutationFn: ({ sessionId, lapId }: { sessionId: string; lapId: string }) =>
      apiClient.deleteLap(sessionId, lapId),
    cacheActions: [
      // Invalidate related queries
      {
        type: "invalidate",
        key: (_, { sessionId }) => ["sessions", sessionId, "laps"],
      },
      {
        type: "invalidate",
        key: (_, { sessionId }) => [
          "sessions",
          sessionId,
          "laps",
          "statistics",
        ],
      },
      {
        type: "invalidate",
        key: (_, { sessionId }) => ["sessions", sessionId, "laps", "best"],
      },
      { type: "invalidate", key: ["dashboard"] },
    ],
  },
});

export const useUpdateLap = lapCrudHooks.useUpdate;
export const useDeleteLap = lapCrudHooks.useDelete;
